import { Injectable } from "@angular/core";
import { NotificationsService } from "angular2-notifications";
import { instanceToPlain, plainToInstance } from "class-transformer";
import * as _ from "lodash";
import { lastValueFrom } from "rxjs";
import { API_ASSOCIATIONS } from "src/common/constants/api.constants";
import { TAPIListResult, TAPIQuery } from "src/common/contracts/api.contracts";
import { AlertApi } from "../../api/alert/alert-instance.api";
import { apiCallWrapper } from "../../api/api.util";
import { HasIdOrExternalValueOrExternalIdOrServiceName } from "../../api/generic.api";
import { Alert } from "../../model/alert/alert.model";
import { AlertTypeEnum } from "../../model/alert/contract/alert-contract";
import { IQueryFilter } from "../../model/query.filter.class";
import { BaseService } from "../base.service";

@Injectable({
  providedIn: "root",
})
export class AlertService extends BaseService {
  constructor(
    private notifications: NotificationsService,
    private alertApi: AlertApi,
  ) {
    super();
  }

  public async create(
    type: AlertTypeEnum,
    externalDealId: string,
    formInstanceId?: string,
    submissionUUID?: string,
    isOnline: number = 0,
  ): Promise<Alert> {
    try {
      const data = await lastValueFrom(
        apiCallWrapper(
          this.alertApi.create({
            type,
            externalDealId,
            isClosed: 0,
            formInstanceId,
            submissionUUID,
            isOnline,
            responseCnt: 1,
          }),
          {
            notificationsService: this.notifications,
            action: "Creating Alert",
          },
        ),
      );

      return data;
    } catch (error) {
      throw error;
    }
  }

  async update(id: number, model: Partial<Alert>): Promise<Alert> {
    try {
      const data = await lastValueFrom(
        apiCallWrapper(this.alertApi.update(id, instanceToPlain(model)), {
          notificationsService: this.notifications,
          action: "Updating Alert",
        }),
      );
      return data;
    } catch (error) {
      throw error;
    }
  }

  async resolveScannedResponseAlert(
    alertId: number,
    formInstanceId?: number,
  ): Promise<{ success: boolean; newAlertId: number | undefined }> {
    try {
      const { success, newAlertId } = await lastValueFrom(
        apiCallWrapper(this.alertApi.resolveScannedResponseAlert(alertId, formInstanceId), {
          notificationsService: this.notifications,
          action: "Resolve Bad Barcode alert",
        }),
      );
      return { success, newAlertId };
    } catch (error) {
      throw error;
    }
  }

  async retrieveScannedResponseImage(alertId: number): Promise<string> {
    try {
      const signedUrl = await lastValueFrom(
        apiCallWrapper(this.alertApi.retrieveScannedResponseImage(alertId), {
          notificationsService: this.notifications,
          action: "Retrieve Bad Barcode image",
        }),
      );
      return signedUrl;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @returns a an alert by id
   */
  async getAlert(id: number): Promise<Alert> {
    try {
      const data = await lastValueFrom(
        apiCallWrapper(this.alertApi.get(id), {
          notificationsService: this.notifications,
          action: "Fetch alert by id",
        }),
      );

      return plainToInstance(Alert, data);
    } catch (error) {
      throw error;
    }
  }

  /**
   * @returns a paginated list of alerts
   */
  async getAlerts(queryParams?: TAPIQuery): Promise<TAPIListResult<Alert>> {
    try {
      const componentQuery = this.transformQueryParamsToIQueryFilter(queryParams);

      const defaultQuery: IQueryFilter = new IQueryFilter(_.cloneDeep(API_ASSOCIATIONS.alert));

      return await this._getAlerts(this.mergeQueryFilters(componentQuery, defaultQuery));
    } catch (error) {
      throw error;
    }
  }

  private async _getAlerts(query: IQueryFilter): Promise<TAPIListResult<Alert>> {
    try {
      const data = await lastValueFrom(
        apiCallWrapper(this.alertApi.list(query), {
          notificationsService: this.notifications,
          action: "Obtaining Alerts",
        }),
      );

      return this.inflateModel<
        Alert,
        Alert & HasIdOrExternalValueOrExternalIdOrServiceName<string>
      >(Alert, data);
    } catch (error) {
      throw error;
    }
  }
}
